import { AnyAction } from 'redux';

// ── Types ───────────────────────────────────────────────────────────

export interface DashboardOverview {
  matters: number;
  tasks_open: number;
  tasks_overdue: number;
  tasks_completed_30d: number;
  reviews_active: number;
  clients: number;
  research_queries_30d: number;
  drafts_30d: number;
  files: number;
  tables: number;
  is_org_admin: boolean;
  projects_active: number;
  project_resources_total: number;
}

export interface DashboardCollaborator {
  name: string;
  initials: string;
  email?: string;
  profile_picture: string | null;
}

/** @deprecated Use DashboardCollaborator */
export type DashboardProjectCollaborator = DashboardCollaborator;

export interface DashboardUrgentTask {
  uuid: string;
  title: string;
  due_date: string | null;
  status: string;
  priority: string;
  matter_title?: string | null;
  collaborators: DashboardCollaborator[];
  collaborator_count: number;
}

export interface DashboardUrgentMatter {
  uuid: string;
  title: string;
  due_date: string | null;
  updated_at: string | null;
  priority: string;
  status: string;
  processing_status: string;
  jurisdiction: string | null;
  collaborators: DashboardCollaborator[];
  collaborator_count: number;
}

export interface DashboardUserProject {
  uuid: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  resource_count: number;
  collaborator_count: number;
  collaborators: DashboardProjectCollaborator[];
  my_access_level: string;
}

export interface DashboardUrgentProject {
  uuid: string;
  title: string;
  due_date: string | null;
  priority: string;
  resource_count: number;
  collaborator_count: number;
}

export interface DashboardProcessingItem {
  uuid: string;
  title: string;
  type: 'matter' | 'review' | 'table';
  stage: string;
  started_at: string | null;
}

export interface DashboardActivity {
  id: number;
  entity_type: string;
  entity_uuid: string;
  action: string;
  details: string;
  link: string;
  created_at: string | null;
}

export interface DashboardCalendarEvent {
  id: number;
  title: string;
  start_time: string | null;
  end_time: string | null;
  all_day: boolean;
  event_type: string;
  color: string | null;
  location: string | null;
}

export interface ProductivityBreakdownItem {
  hours: number;
  count: number;
  detail: string;
}

export interface DashboardProductivity {
  tasks_completed_30d: number;
  matters_analyzed_30d: number;
  reviews_completed_30d: number;
  tables_processed_30d: number;
  research_queries_30d: number;
  drafts_generated_30d: number;
  hours_saved: number;
  cost_saved: number;
  breakdown: {
    matters: ProductivityBreakdownItem;
    reviews: ProductivityBreakdownItem;
    drafts: ProductivityBreakdownItem;
    research: ProductivityBreakdownItem;
    tables: ProductivityBreakdownItem;
  };
}

export interface DashboardState {
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  overview: DashboardOverview | null;
  userProjects: DashboardUserProject[];
  urgentTasks: DashboardUrgentTask[];
  urgentMatters: DashboardUrgentMatter[];
  urgentProjects: DashboardUrgentProject[];
  pendingAccessRequestsCount: number;
  processingItems: DashboardProcessingItem[];
  recentActivity: DashboardActivity[];
  upcomingEvents: DashboardCalendarEvent[];
  productivity: DashboardProductivity | null;
}

// ── Initial state ───────────────────────────────────────────────────

const initialState: DashboardState = {
  loading: false,
  error: null,
  lastFetched: null,
  overview: null,
  userProjects: [],
  urgentTasks: [],
  urgentMatters: [],
  urgentProjects: [],
  pendingAccessRequestsCount: 0,
  processingItems: [],
  recentActivity: [],
  upcomingEvents: [],
  productivity: null,
};

// ── Reducer ─────────────────────────────────────────────────────────

export const dashboardReducer = (
  state: DashboardState = initialState,
  action: AnyAction
): DashboardState => {
  switch (action.type) {
    case 'FETCH_DASHBOARD_REQUEST':
      return { ...state, loading: true, error: null };

    case 'FETCH_DASHBOARD_SUCCESS':
      return {
        ...state,
        loading: false,
        error: null,
        lastFetched: Date.now(),
        overview: action.payload.overview,
        userProjects: action.payload.user_projects || [],
        urgentTasks: action.payload.urgent_tasks || [],
        urgentMatters: action.payload.urgent_matters || [],
        urgentProjects: action.payload.urgent_projects || [],
        pendingAccessRequestsCount: action.payload.pending_access_requests_count || 0,
        processingItems: action.payload.processing_items || [],
        recentActivity: action.payload.recent_activity || [],
        upcomingEvents: action.payload.upcoming_events || [],
        productivity: action.payload.productivity || null,
      };

    case 'FETCH_DASHBOARD_FAILURE':
      return { ...state, loading: false, error: action.payload };

    case 'DASHBOARD_COMPLETE_TASK': {
      const taskUuid = action.payload;
      const updatedTasks = state.urgentTasks.filter(t => t.uuid !== taskUuid);
      const removedTask = state.urgentTasks.find(t => t.uuid === taskUuid);
      const now = new Date();

      let updatedOverview = state.overview;
      if (updatedOverview && removedTask) {
        const wasOverdue = removedTask.due_date && new Date(removedTask.due_date) < now;
        updatedOverview = {
          ...updatedOverview,
          tasks_open: Math.max(0, updatedOverview.tasks_open - 1),
          tasks_overdue: wasOverdue
            ? Math.max(0, updatedOverview.tasks_overdue - 1)
            : updatedOverview.tasks_overdue,
          tasks_completed_30d: updatedOverview.tasks_completed_30d + 1,
        };
      }

      return {
        ...state,
        urgentTasks: updatedTasks,
        overview: updatedOverview,
      };
    }

    default:
      return state;
  }
};
